import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import { APP_CONFIG, IAppConfig } from './../../../../app.config';
import { DataService, IModel } from '../../../../core/service/base/data.service';
import { MessageService } from '../../../../core/service/message.service';
import { ProgressBarService } from '../../../../core/service/progress-bar.service';
import { RentInvoicingOrderCreateModel } from '../../../model/rent-invoicing/rent-invoicing-order-create-model';
import { RentInvoicingOrderCreateSingleModel } from '../../../model/rent-invoicing/rent-invoicing-order-create-single-model';
import { RentInvoicingOrderCreateViewModel } from '../../../model/rent-invoicing/rent-invoicing-order-create-view-model';
import { RentInvoicingOrderDetails } from '../../../model/rent-invoicing/rent-invoicing-order-details';
import { RentInvoicingOrderUpdateModel } from '../../../model/rent-invoicing/rent-invoicing-order-update-model';

@Injectable()
export class RentInvoicingOrderDetailsService extends DataService<
  RentInvoicingOrderDetails,
  RentInvoicingOrderCreateModel,
  RentInvoicingOrderUpdateModel
> {
  constructor(
    client: HttpClient,
    @Inject(APP_CONFIG) config: IAppConfig,
    private _messageService: MessageService,
    progressBar: ProgressBarService
  ) {
    super(client, config, _messageService, RentInvoicingOrderDetails, progressBar, 'rent-invoicing-order');
  }

  getCreateData(): Observable<RentInvoicingOrderCreateViewModel> {
    return this.client.get<RentInvoicingOrderCreateViewModel>(`${this.endpoint}/default`);
  }

  override createNew(model: RentInvoicingOrderCreateModel, options: { notifyUser: boolean } = { notifyUser: true }): Observable<IModel> {
    const { appendixes, ...cleanModel } = model;

    this.progressBar.start();
    const formData = new FormData();
    formData.append('jsonData', JSON.stringify(cleanModel));

    if (appendixes) {
      for (const file of appendixes) {
        formData.append('appendixes[]', file, file.name);
      }
    }
    return this.client.post<IModel>(`${this.endpoint}`, formData).pipe(
      tap(() => {
        if (options?.notifyUser) this._messageService.showSuccess();
      }),
      finalize(() => {
        this.progressBar.stop();
      })
    );
  }

  attestAll(id: string): Observable<string> {
    this.progressBar.start();
    return this.client.post<string>(`${this.endpoint}/attest-all/${id}`, null).pipe(
      tap(x => {
        this._messageService.showSuccess('SUCCESS.INVOICES_ATTESTED');
      }),
      finalize(() => {
        this.progressBar.stop();
      })
    );
  }

  getInvoiceStatistics(id: string): Observable<any[]> {
    return this.client.get<any[]>(`${this.endpoint}/statistics/${id}`);
  }

  updateExisting(model: RentInvoicingOrderUpdateModel, options: { notifyUser: boolean } = { notifyUser: true }): Observable<any> {
    this.progressBar.start();
    return this.client.put<any>(`${this.endpoint}`, JSON.stringify(model)).pipe(
      tap(() => {
        if (options?.notifyUser) this._messageService.showSuccess();
      }),
      finalize(() => {
        this.progressBar.stop();
      })
    );
  }

  getPollingUpdate(id: string): Observable<RentInvoicingOrderDetails> {
    return this.client.get<RentInvoicingOrderDetails>(`${this.endpoint}/${id}`).pipe(map(item => this.createModel(item)));
  }

  override getFilteredList(filter: any): Observable<RentInvoicingOrderDetails[]> {
    throw new Error('Not supported. Use RentInvoicingOrderService instead.');
  }
}
